import React, { useState } from 'react';
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Box,
  Typography
} from '@mui/material';
import {
  DatePicker,
  TimePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CalendarToday, AccessTime, Clear } from '@mui/icons-material';
import { format, isValid } from 'date-fns';

interface DateTimePickerProps {
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  showTime?: boolean;
  minDate?: Date;
  maxDate?: Date;
  fullWidth?: boolean;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  label,
  value,
  onChange,
  error = false,
  helperText,
  required = false,
  disabled = false,
  showTime = true,
  minDate,
  maxDate,
  fullWidth = true
}) => {
  const [open, setOpen] = useState(false);
  const [tempDate, setTempDate] = useState<Date | null>(value);
  const [tempTime, setTempTime] = useState<Date | null>(value);

  const handleOpen = () => {
    setTempDate(value);
    setTempTime(value);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = () => {
    if (tempDate && showTime && tempTime) {
      // Combine date and time
      const combinedDateTime = new Date(tempDate);
      combinedDateTime.setHours(tempTime.getHours());
      combinedDateTime.setMinutes(tempTime.getMinutes());
      onChange(combinedDateTime);
    } else if (tempDate) {
      onChange(tempDate);
    } else {
      onChange(null);
    }
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setOpen(false);
  };

  const formatDisplayValue = (date: Date | null) => {
    if (!date || !isValid(date)) return '';
    
    if (showTime) {
      return format(date, 'MMM dd, yyyy HH:mm');
    }
    return format(date, 'MMM dd, yyyy');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TextField
        label={label}
        value={formatDisplayValue(value)}
        onClick={handleOpen}
        error={error}
        helperText={helperText}
        required={required}
        disabled={disabled}
        fullWidth={fullWidth}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {value && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange(null);
                  }}
                  disabled={disabled}
                >
                  <Clear fontSize="small" />
                </IconButton>
              )}
              <IconButton
                onClick={handleOpen}
                disabled={disabled}
                size="small"
              >
                {showTime ? <AccessTime /> : <CalendarToday />}
              </IconButton>
            </Box>
          )
        }}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          '& .MuiInputBase-input': {
            cursor: disabled ? 'default' : 'pointer'
          }
        }}
      />

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">
            {showTime ? 'Select Date & Time' : 'Select Date'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={showTime ? 6 : 12}>
              <DatePicker
                label="Date"
                value={tempDate}
                onChange={setTempDate}
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined'
                  }
                }}
              />
            </Grid>
            {showTime && (
              <Grid item xs={12} md={6}>
                <TimePicker
                  label="Time"
                  value={tempTime}
                  onChange={setTempTime}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined'
                    }
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} color="error">
            Clear
          </Button>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirm} 
            variant="contained"
            sx={{ 
              backgroundColor: '#F8B133',
              '&:hover': { backgroundColor: '#f29100' }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default DateTimePicker;
